import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { getPhotoById, updatePhoto, uploadImage, deleteImage } from '../../services/photoService';
import { Photo, PhotoInput, CropData } from '../../types/Photo';

const Form = styled.form`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 24px 0;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #555;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 150px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const FileInput = styled.input`
  width: 100%;
  padding: 12px;
  font-size: 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #0070f3;
  }
`;

const ImagePreview = styled.div`
  margin-top: 12px;
  max-width: 100%;
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CameraDetailsGroup = styled.div`
  background: #f8f8f8;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 24px;
`;

const CameraDetailsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #333;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === 'primary'
      ? `
    background: #0070f3;
    color: white;

    &:hover:not(:disabled) {
      background: #0051cc;
    }
  `
      : `
    background: #f0f0f0;
    color: #333;

    &:hover:not(:disabled) {
      background: #e0e0e0;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  margin-bottom: 24px;
  border-radius: 4px;
  font-size: 14px;

  ${(props) =>
    props.type === 'success'
      ? `
    background: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
  `
      : `
    background: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #666;
`;

const CropButton = styled.button`
  margin-top: 12px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #218838;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const CropModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  flex-direction: column;
  z-index: 2000;
  overflow: hidden;
  pointer-events: auto;
`;

const CropContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 80px);
  min-height: 400px;
  touch-action: none;

  * {
    pointer-events: auto !important;
    user-select: auto !important;
  }

  .reactEasyCrop_Container,
  .reactEasyCrop_CropArea,
  .reactEasyCrop_Image {
    pointer-events: auto !important;
    cursor: move !important;
  }
`;

const CropControls = styled.div`
  padding: 20px;
  background: #fff;
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  height: 80px;
  box-sizing: border-box;
`;

const ZoomSlider = styled.input`
  width: 200px;
`;

const CropActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${(props) =>
    props.variant === 'secondary'
      ? `
    background: #6c757d;
    color: white;

    &:hover {
      background: #5a6268;
    }
  `
      : `
    background: #0070f3;
    color: white;

    &:hover {
      background: #0051cc;
    }
  `}
`;

const PhotoEditForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [photo, setPhoto] = useState<Photo | null>(null);
  const [formData, setFormData] = useState<Partial<PhotoInput>>({
    title: '',
    description: '',
    location: '',
    date: '',
    cameraDetails: {},
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Crop state
  const [showCropModal, setShowCropModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropData, setCropData] = useState<CropData | undefined>(undefined);

  useEffect(() => {
    const loadPhoto = async () => {
      if (!id) return;

      try {
        const fetchedPhoto = await getPhotoById(id);
        if (fetchedPhoto) {
          setPhoto(fetchedPhoto);
          const dateStr = typeof fetchedPhoto.date === 'string'
            ? fetchedPhoto.date.split('T')[0]
            : new (Date as any)(fetchedPhoto.date).toISOString().split('T')[0];

          setFormData({
            title: fetchedPhoto.title,
            description: fetchedPhoto.description,
            location: fetchedPhoto.location,
            date: dateStr,
            cameraDetails: fetchedPhoto.cameraDetails,
          });
          setPreviewUrl(fetchedPhoto.imageURL);
          setCropData(fetchedPhoto.crop);
        } else {
          setMessage({ type: 'error', text: 'Photo not found' });
        }
      } catch (error) {
        console.error('Error loading photo:', error);
        setMessage({ type: 'error', text: 'Failed to load photo' });
      } finally {
        setLoading(false);
      }
    };

    loadPhoto();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCameraDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      cameraDetails: {
        ...prev.cameraDetails,
        [name]: value,
      },
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
      // Reset crop when changing image
      setCropData(undefined);
    }
  };

  const onCropComplete = useCallback((croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleOpenCrop = () => {
    // Reset crop position and zoom when opening
    if (cropData) {
      // If there's existing crop data, we could initialize with it
      // but for now we'll start fresh
      setCrop({ x: 0, y: 0 });
      setZoom(cropData.zoom || 1);
    } else {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
    setShowCropModal(true);
  };

  const handleCancelCrop = () => {
    setShowCropModal(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const handleSaveCrop = () => {
    if (croppedAreaPixels) {
      setCropData({
        x: croppedAreaPixels.x,
        y: croppedAreaPixels.y,
        width: croppedAreaPixels.width,
        height: croppedAreaPixels.height,
        zoom,
      });
      setShowCropModal(false);
    }
  };

  const handleRemoveCrop = () => {
    setCropData(undefined);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!id || !photo) return;

    if (!formData.title || !formData.location) {
      setMessage({ type: 'error', text: 'Please fill in all required fields' });
      return;
    }

    try {
      setUpdating(true);

      let imageURL = photo.imageURL;

      // If a new image was selected, upload it and delete the old one
      if (imageFile) {
        imageURL = await uploadImage(imageFile, 'photos');

        // Delete old image
        try {
          await deleteImage(photo.imageURL);
        } catch (error) {
          console.warn('Could not delete old image:', error);
        }
      }

      // Update photo metadata
      const updateData: any = {
        imageURL,
        title: formData.title!,
        description: formData.description || '',
        location: formData.location!,
        date: formData.date || new Date().toISOString(),
        cameraDetails: formData.cameraDetails || {},
        ...(cropData !== undefined && { crop: cropData }),
      };

      await updatePhoto(id, updateData);

      setMessage({ type: 'success', text: 'Photo updated successfully!' });

      // Navigate back to admin list after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 1500);
    } catch (error) {
      console.error('Error updating photo:', error);
      setMessage({ type: 'error', text: 'Failed to update photo. Please try again.' });
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin');
  };

  if (loading) {
    return <LoadingContainer>Loading photo...</LoadingContainer>;
  }

  if (!photo) {
    return <LoadingContainer>Photo not found</LoadingContainer>;
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Title>Edit Photo</Title>

      {message && <Message type={message.type}>{message.text}</Message>}

      <FormGroup>
        <Label htmlFor="image">Change Image (optional)</Label>
        <FileInput
          type="file"
          id="image"
          accept="image/*"
          onChange={handleFileChange}
        />
        {previewUrl && (
          <>
            <ImagePreview>
              <img src={previewUrl} alt="Preview" />
            </ImagePreview>
            <CropButton type="button" onClick={handleOpenCrop}>
              {cropData ? 'Edit Crop' : 'Crop Image'}
            </CropButton>
            {cropData && (
              <CropButton type="button" onClick={handleRemoveCrop} style={{ marginLeft: '8px', background: '#dc3545' }}>
                Remove Crop
              </CropButton>
            )}
          </>
        )}
      </FormGroup>

      <CropModal isOpen={showCropModal} onClick={(e) => e.target === e.currentTarget && handleCancelCrop()}>
        <CropContainer onClick={(e) => e.stopPropagation()}>
          <Cropper
            image={previewUrl}
            crop={crop}
            zoom={zoom}
            aspect={undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            restrictPosition={false}
            showGrid={true}
            zoomSpeed={0.1}
            style={{
              containerStyle: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                cursor: 'move',
              },
              mediaStyle: {
                pointerEvents: 'auto',
              },
              cropAreaStyle: {
                border: '2px solid rgba(255, 255, 255, 0.5)',
              },
            }}
          />
        </CropContainer>
        <CropControls>
          <label>Zoom:</label>
          <ZoomSlider
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
          <CropActionButton variant="secondary" type="button" onClick={handleCancelCrop}>
            Cancel
          </CropActionButton>
          <CropActionButton type="button" onClick={handleSaveCrop}>
            Save Crop
          </CropActionButton>
        </CropControls>
      </CropModal>

      <FormGroup>
        <Label htmlFor="title">Title *</Label>
        <Input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleInputChange}
          required
          placeholder="Enter photo title"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="location">Location *</Label>
        <Input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleInputChange}
          required
          placeholder="e.g., Yosemite National Park, California"
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="date">Date</Label>
        <Input
          type="date"
          id="date"
          name="date"
          value={formData.date as string}
          onChange={handleInputChange}
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="description">Description</Label>
        <TextArea
          id="description"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          placeholder="Tell the story behind this photo..."
        />
      </FormGroup>

      <CameraDetailsGroup>
        <CameraDetailsTitle>Camera Details</CameraDetailsTitle>
        <Row>
          <FormGroup>
            <Label htmlFor="make">Camera Make</Label>
            <Input
              type="text"
              id="make"
              name="make"
              value={formData.cameraDetails?.make || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., Canon"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="model">Camera Model</Label>
            <Input
              type="text"
              id="model"
              name="model"
              value={formData.cameraDetails?.model || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., EOS R5"
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="lens">Lens</Label>
            <Input
              type="text"
              id="lens"
              name="lens"
              value={formData.cameraDetails?.lens || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., RF 24-70mm f/2.8L"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="focalLength">Focal Length</Label>
            <Input
              type="text"
              id="focalLength"
              name="focalLength"
              value={formData.cameraDetails?.focalLength || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., 50mm"
            />
          </FormGroup>
        </Row>

        <Row>
          <FormGroup>
            <Label htmlFor="aperture">Aperture</Label>
            <Input
              type="text"
              id="aperture"
              name="aperture"
              value={formData.cameraDetails?.aperture || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., f/2.8"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="shutterSpeed">Shutter Speed</Label>
            <Input
              type="text"
              id="shutterSpeed"
              name="shutterSpeed"
              value={formData.cameraDetails?.shutterSpeed || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., 1/250s"
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="iso">ISO</Label>
            <Input
              type="text"
              id="iso"
              name="iso"
              value={formData.cameraDetails?.iso || ''}
              onChange={handleCameraDetailsChange}
              placeholder="e.g., ISO 400"
            />
          </FormGroup>
        </Row>
      </CameraDetailsGroup>

      <ButtonGroup>
        <Button type="button" variant="secondary" onClick={handleCancel} disabled={updating}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={updating}>
          {updating ? 'Updating...' : 'Update Photo'}
        </Button>
      </ButtonGroup>
    </Form>
  );
};

export default PhotoEditForm;
